import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Users, TrendingUp, Filter, List, Grid, Download, BarChart2 } from 'lucide-react';
import toast from 'react-hot-toast';
import statisticsService from '../../services/statisticsService';
import { users as userService } from '../../services/apiService';
import { getTalentTypeOptions } from '../../utils/talentTypeUtils';
import { Loader } from '../UI/LoadingSpinner';
import ManagerFilterPanel from './ManagerFilterPanel';
import { PAGE_KEYS } from '../../constants/savedFilterPageKeys';
import { Pagination, Autocomplete } from '@anyemp/ui-kit';
import { PAGINATION_OPTIONS } from '../../utils/paginationConstants';
import Avatar from '../UI/Avatar';
import ManagerDetailsModal from './ManagerDetailsModal';
import { exportMatrixViewCSV, exportAddedWithTalentsCSV } from '../../utils/statisticsExportUtils';
import StatisticsCharts from './StatisticsCharts';
import MatrixTable from './MatrixTable';
import StatisticsTable from './StatisticsTable';

const ManagerTalentsStats = () => {
  // Функция для получения первого и последнего дня текущего месяца
  const getCurrentMonthDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: formatDate(firstDay),
      endDate: formatDate(lastDay)
    };
  };

  const currentMonthDates = getCurrentMonthDates();

  const [stats, setStats] = useState([]);
  const [globalTotals, setGlobalTotals] = useState(null); // { grand_total, by_period: [{period, count}] }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);
  const [managers, setManagers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'matrix'
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20
  });
  const [filters, setFilters] = useState({
    startDate: currentMonthDates.startDate,
    endDate: currentMonthDates.endDate,
    talent_types: [],
    manager_ids: [],
    limit: 20,
    offset: 0,
    period: ''
  });
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const talentTypeOptions = getTalentTypeOptions();


  useEffect(() => {
    fetchStats();
  }, [filters.limit, filters.offset]);

  useEffect(() => {
    fetchManagers();
  }, []);

  // При переключении на matrix/charts — автоматически ставим period=day и делаем fetch
  useEffect(() => {
    if ((viewMode === 'matrix' || viewMode === 'charts') && !filters.period) {
      setFilters(prev => ({ ...prev, period: 'day' }));
    }
  }, [viewMode]);

  // При изменении period в matrix/charts-режиме — перезагружаем данные
  useEffect(() => {
    if ((viewMode === 'matrix' || viewMode === 'charts') && filters.period) {
      fetchStats();
    }
  }, [filters.period, viewMode]);

  const fetchManagers = async () => {
    try {
      setManagersLoading(true);
      const response = await userService.getAllUsers();

      if (response.success && response.data) {
        const usersList = response.data.users || response.data;
        const managerOptions = Array.isArray(usersList)
          ? usersList.map(user => ({
              value: String(user.id),
              label: user.name || user.email || user.id
            }))
          : [];
        setManagers(managerOptions);
      }
    } catch (err) {
      console.error('Error fetching managers:', err);
      toast.error('Failed to load managers list');
    } finally {
      setManagersLoading(false);
    }
  };

  const fetchStats = async (overrideFilters = null) => {
    try {
      setLoading(true);
      setError(null);

      const f = overrideFilters ?? filters;

      // В matrix-режиме всегда передаем period (минимум day)
      const effectivePeriod = viewMode === 'matrix'
        ? (f.period || 'day')
        : f.period;

      const params = {
        ...f,
        talent_types: f.talent_types.length > 0 ? f.talent_types : undefined,
        manager_ids: f.manager_ids.length > 0 ? f.manager_ids : undefined,
        startDate: f.startDate || undefined,
        endDate: f.endDate || undefined,
        period: effectivePeriod || undefined
      };

      // В matrix/charts-режиме загружаем основные данные + глобальные тотали параллельно
      const requests = [statisticsService.getManagerTalentsAdded(params)];

      if (viewMode === 'matrix' || viewMode === 'charts') {
        const totalsParams = {
          startDate: params.startDate,
          endDate: params.endDate,
          talent_types: params.talent_types,
          manager_ids: params.manager_ids,
          period: params.period
        };
        requests.push(statisticsService.getManagerTalentsTotals(totalsParams));
      }

      const [response, totalsResponse] = await Promise.all(requests);

      if (response.success && response.data) {
        setStats(response.data);
        if (response.pagination) {
          setPagination({
            current_page: Math.floor(f.offset / f.limit) + 1,
            total_pages: Math.ceil(response.pagination.total / f.limit),
            total_items: response.pagination.total,
            items_per_page: f.limit
          });
        }
      }

      if (totalsResponse?.success && totalsResponse?.data) {
        setGlobalTotals(totalsResponse.data);
      } else if (totalsResponse?.data) {
        setGlobalTotals(totalsResponse.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
      setError(error.message || 'Failed to load statistics');
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setFilters(prev => ({ ...prev, offset: 0 }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
    fetchStats();
  };

  const handleResetFilters = () => {
    const resetFilters = {
      startDate: currentMonthDates.startDate,
      endDate: currentMonthDates.endDate,
      talent_types: [],
      manager_ids: [],
      limit: 20,
      offset: 0,
      period: (viewMode === 'matrix' || viewMode === 'charts') ? 'day' : ''
    };
    setFilters(resetFilters);
    setPagination({ current_page: 1, total_pages: 1, total_items: 0, items_per_page: 20 });
    fetchStats(resetFilters);
  };

  const handlePageChange = (newPage) => {
    const newOffset = (newPage - 1) * filters.limit;
    setFilters(prev => ({ ...prev, offset: newOffset }));
  };

  const handleLimitChange = (newLimit) => {
    setFilters(prev => ({ ...prev, limit: newLimit, offset: 0 }));
  };

  const handleManagerClick = useCallback((managerId) => {
    setSelectedManager(managerId);
  }, []);

  const handleViewModeChange = (mode) => {
    if (mode === 'matrix' || mode === 'charts') {
      setLoading(true);
    }
    setViewMode(mode);
  };

  const handleExport = async () => {
    if (viewMode === 'list') {
      setExporting(true);
      try {
        await exportAddedWithTalentsCSV(
          stats,
          (managerId, params) => statisticsService.getManagerDetails(managerId, params),
          { startDate: filters.startDate, endDate: filters.endDate }
        );
      } catch (err) {
        toast.error('Failed to export CSV');
        console.error('Export error:', err);
      } finally {
        setExporting(false);
      }
    } else {
      exportMatrixViewCSV(matrixData, matrixColumnTotals, matrixGrandTotal, generateDateColumns);
    }
  };

  // Данные менеджера могут быть вложены в manager.user (структура API Gateway getUserById)
  // или напрямую в manager (структура getUsersByIds через createLookupMap)
  const getManagerInfo = (item) => {
    const m = item.manager;
    if (!m) return { name: null, email: null, avatar: null };
    const user = m.user || m;
    return {
      name: user.fullName || user.name || null,
      email: user.email || null,
      avatar: user.avatar || null
    };
  };

  // --- Matrix helpers ---

  // Генерация колонок дат из диапазона фильтров
  const generateDateColumns = useMemo(() => {
    if (!filters.startDate || !filters.endDate) return [];

    const period = filters.period || 'day';
    const columns = [];

    if (period === 'day') {
      const start = new Date(filters.startDate + 'T00:00:00');
      const end = new Date(filters.endDate + 'T00:00:00');

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dayOfWeek = d.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const day = d.getDate();
        const month = d.toLocaleDateString('en-US', { month: 'short' });

        columns.push({
          key: dateStr,
          label: String(day),
          sublabel: isWeekend ? 'wknd' : month,
          dateStr,
          isWeekend
        });
      }
    } else if (period === 'week') {
      // DATE_TRUNC('week') в PostgreSQL всегда возвращает ПОНЕДЕЛЬНИК
      // Поэтому нормализуем startDate к ближайшему понедельнику (или тому же, если уже понедельник)
      const start = new Date(filters.startDate + 'T00:00:00');
      const end = new Date(filters.endDate + 'T00:00:00');

      // Найти первый понедельник <= start
      const getMonday = (date) => {
        const d = new Date(date);
        const day = d.getDay(); // 0=Sun, 1=Mon...
        const diff = day === 0 ? -6 : 1 - day; // сдвиг до понедельника
        d.setDate(d.getDate() + diff);
        return d;
      };

      let weekNum = 1;
      for (let d = getMonday(start); d <= end; d.setDate(d.getDate() + 7)) {
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

        columns.push({
          key: dateStr,
          label: String(weekNum),
          sublabel: 'wknd',
          dateStr,
          isWeekend: false
        });
        weekNum++;
      }
    } else if (period === 'month') {
      // Группировка по месяцам: label = "Jan", sublabel = "2026"
      const start = new Date(filters.startDate + 'T00:00:00');
      const end = new Date(filters.endDate + 'T00:00:00');
      const startMonth = new Date(start.getFullYear(), start.getMonth(), 1);
      const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);

      for (let d = new Date(startMonth); d <= endMonth; d.setMonth(d.getMonth() + 1)) {
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
        const month = d.toLocaleDateString('en-US', { month: 'short' });
        const year = d.getFullYear();
        columns.push({
          key: dateStr,
          label: month,
          sublabel: String(year),
          dateStr,
          isWeekend: false
        });
      }
    }

    return columns;
  }, [filters.startDate, filters.endDate, filters.period]);

  // Строим lookup: { managerId: { "2026-02-01": 3, ... } }
  const matrixData = useMemo(() => {
    if (!stats.length || !generateDateColumns.length) return [];

    return stats.map(manager => {
      const periodMap = {};
      (manager.by_period || []).forEach(p => {
        // Нормализуем ключ к YYYY-MM-DD (API возвращает ISO строку с временем)
        const dateKey = typeof p.period === 'string' ? p.period.split('T')[0] : p.period;
        periodMap[dateKey] = parseInt(p.count) || 0;
      });

      const days = {};
      generateDateColumns.forEach(col => {
        days[col.dateStr] = periodMap[col.dateStr] || 0;
      });

      return { ...manager, days };
    });
  }, [stats, generateDateColumns]);

  // Суммы по каждой колонке — из globalTotals (API, все менеджеры независимо от пагинации)
  const matrixColumnTotals = useMemo(() => {
    if (!globalTotals?.by_period || !generateDateColumns.length) return {};
    const periodMap = {};
    globalTotals.by_period.forEach(p => {
      const dateKey = typeof p.period === 'string' ? p.period.split('T')[0] : p.period;
      periodMap[dateKey] = parseInt(p.count) || 0;
    });
    const totals = {};
    generateDateColumns.forEach(col => {
      totals[col.dateStr] = periodMap[col.dateStr] || 0;
    });
    return totals;
  }, [globalTotals, generateDateColumns]);

  const matrixGrandTotal = globalTotals?.grand_total ?? 0;

  const rowKeyManager = useCallback(
    (item) => item.manager?.id || item.manager?.email || JSON.stringify(item),
    []
  );

  const visibleColumns = useMemo(
    () => ({
      manager: true,
      total_talents: true,
      candidates: true,
      employees: true,
      presales: true,
      by_period: !!filters.period,
    }),
    [filters.period]
  );

  // --- List view columns ---
  const tableColumns = useMemo(() => {
    const cols = [
    {
      label: 'Manager',
      key: 'manager',
      sortable: false,
      render: (item) => (
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => handleManagerClick(item.manager_id)}
        >
          {(() => { const mgr = getManagerInfo(item); return (<>
            <Avatar avatar={mgr.avatar} name={mgr.name || item.manager_id} email={mgr.email} size="sm" />
            <div className="flex flex-col">
              <span className="font-medium text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 transition-colors whitespace-nowrap">
                {mgr.name || `Manager ${item.manager_id}`}
              </span>
              {mgr.email && (
                <span className="text-xs text-gray-400 whitespace-nowrap">{mgr.email}</span>
              )}
            </div>
          </>); })()}
        </div>
      )
    },
    {
      label: 'Total Talents',
      key: 'total_talents',
      sortable: true,
      render: (item) => (
        <div className="flex flex-wrap items-center gap-2">
          <Users className="h-4 w-4 text-blue-500" />
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {item.total_talents}
          </span>
        </div>
      )
    },
    {
      label: 'Candidates',
      key: 'candidates',
      sortable: false,
      render: (item) => (
        <span className="text-gray-900 dark:text-gray-100">
          {item.by_type?.candidate || 0}
        </span>
      )
    },
    {
      label: <span title="Converted from candidates">Employees</span>,
      key: 'employees',
      sortable: false,
      render: (item) => (
        item.conversions?.to_employee > 0
          ? <span className="text-sm text-green-600 dark:text-green-400 whitespace-nowrap">
              ↑{item.conversions.to_employee} from cand.
            </span>
          : <span className="text-gray-400 dark:text-gray-600">—</span>
      )
    },
    {
      label: <span title="Converted from candidates">Presales</span>,
      key: 'presales',
      sortable: false,
      render: (item) => (
        item.conversions?.to_presale > 0
          ? <span className="text-sm text-green-600 dark:text-green-400 whitespace-nowrap">
              ↑{item.conversions.to_presale} from cand.
            </span>
          : <span className="text-gray-400 dark:text-gray-600">—</span>
      )
    }
  ];

  if (filters.period && stats.some(s => s.by_period && s.by_period.length > 0)) {
    cols.push({
      label: 'By Periods',
      key: 'by_period',
      sortable: false,
      render: (item) => {
        if (!item.by_period || item.by_period.length === 0) {
          return <span className="text-gray-400">No data</span>;
        }
        return (
          <div className="flex flex-col gap-1">
            {item.by_period.slice(0, 3).map((period, index) => (
              <div key={index} className="text-xs text-gray-600 dark:text-gray-400">
                {new Date(period.period).toLocaleDateString()}: {period.count}
              </div>
            ))}
            {item.by_period.length > 3 && (
              <span className="text-xs text-gray-400">
                +{item.by_period.length - 3} more
              </span>
            )}
          </div>
        );
      }
    });
  }
    return cols;
  }, [filters.period, stats, handleManagerClick]);

  if (loading && stats.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <TrendingUp className="h-8 w-8 text-primary-600" />
          <div>
            <h1 className="break-words text-2xl font-bold text-gray-900 dark:text-gray-100">
              Managers Statistics
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Number of talents added by each manager
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex max-w-full overflow-x-auto rounded-lg border border-gray-300 dark:border-gray-600">
            <button
              onClick={() => handleViewModeChange('list')}
              title="List view"
              className={`flex shrink-0 items-center gap-1.5 px-3 py-2 text-sm transition-colors ${
                viewMode === 'list'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <List className="h-4 w-4" />
              <span>List</span>
            </button>
            <button
              onClick={() => handleViewModeChange('matrix')}
              title="Matrix view by days"
              className={`flex shrink-0 items-center gap-1.5 border-l px-3 py-2 text-sm transition-colors border-gray-300 dark:border-gray-600 ${
                viewMode === 'matrix'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Grid className="h-4 w-4" />
              <span>Matrix</span>
            </button>
            <button
              onClick={() => handleViewModeChange('charts')}
              title="Charts view"
              className={`flex shrink-0 items-center gap-1.5 border-l px-3 py-2 text-sm transition-colors border-gray-300 dark:border-gray-600 ${
                viewMode === 'charts'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <BarChart2 className="h-4 w-4" />
              <span>Charts</span>
            </button>
          </div>

          {/* Export CSV */}
          <button
            onClick={handleExport}
            disabled={!stats.length || exporting}
            title="Export to CSV"
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4" />
            <span>{exporting ? 'Exporting...' : 'CSV'}</span>
          </button>

          {/* Filters Toggle */}
          <button
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors"
          >
            <Filter className="h-4 w-4" />
            <span>Filters</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      {filtersExpanded && (
        <ManagerFilterPanel
          filters={filters}
          managers={managers}
          managersLoading={managersLoading}
          viewMode={viewMode}
          onFilterChange={(updates) => setFilters(prev => ({ ...prev, ...updates, offset: 0 }))}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
          pageKey={PAGE_KEYS.STATS_MANAGER_TALENTS}
          savedFilterKeys={['startDate', 'endDate', 'manager_ids', 'period', 'talent_types']}
          extraFilters={
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Talent Types
              </label>
              <Autocomplete
                multiple
                clearable
                fullWidth
                options={talentTypeOptions}
                value={filters.talent_types}
                onChange={(value) => setFilters(prev => ({ ...prev, talent_types: value || [], offset: 0 }))}
                placeholder="All types"
              />
            </div>
          }
        />
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* LIST VIEW */}
      {viewMode === 'list' && (
        <StatisticsTable
          data={stats}
          columns={tableColumns}
          isLoading={loading}
          rowKey={rowKeyManager}
          visibleColumns={visibleColumns}
          footer={
            <Pagination
              currentPage={pagination.current_page}
              totalPages={pagination.total_pages}
              totalItems={pagination.total_items}
              itemsPerPage={pagination.items_per_page}
              onPageChange={handlePageChange}
              showResults="range"
              perPageOptions={PAGINATION_OPTIONS}
              perPage={filters.limit}
              onPerPageChange={(v) => handleLimitChange(parseInt(v))}
              showOnSinglePage
              hasFirstLast
              hasGoToPage
            />
          }
        />
      )}

      {/* MATRIX VIEW */}
      {viewMode === 'matrix' && (
        <MatrixTable
          data={matrixData}
          loading={loading}
          dateColumns={generateDateColumns}
          columnTotals={matrixColumnTotals}
          grandTotal={matrixGrandTotal}
          totalKey="total_talents"
          startIndex={filters.offset}
          onManagerClick={(row) => handleManagerClick(row.manager_id)}
          getManagerInfo={getManagerInfo}
          pagination={pagination}
          onPageChange={handlePageChange}
          itemsPerPage={filters.limit}
        />
      )}

      {/* CHARTS VIEW */}
      {viewMode === 'charts' && (
        loading ? (
          <div className="flex justify-center items-center min-h-[400px]">
            <Loader />
          </div>
        ) : stats.length === 0 ? (
          <div className="flex justify-center items-center min-h-[300px] text-gray-400 dark:text-gray-500">
            No data for selected filters
          </div>
        ) : (
          <StatisticsCharts
            stats={stats}
            globalTotals={globalTotals}
            getManagerInfo={getManagerInfo}
          />
        )
      )}

      {/* Manager Details Modal */}
      {selectedManager && (
        <ManagerDetailsModal
          managerId={selectedManager}
          filters={filters}
          onClose={() => setSelectedManager(null)}
        />
      )}
    </div>
  );
};

export default ManagerTalentsStats;
